// Admin — Edit Shift Times modal.
// Spec: docs/admin-shift-management.md §2.2 BR-10..BR-15, §3.2, §7.3

import { useEffect, useState } from 'react';
import { Descriptions, Form, Modal, TimePicker, notification } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { AdminEditShiftDto, Shift } from 'types/shift';
import { mapShiftError } from '../helpers/shift-error';
import { sameTime } from '../helpers/shift-time';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';

// Required to parse 'HH:mm' strings into dayjs objects for the TimePicker.
dayjs.extend(customParseFormat);

interface Props {
  open: boolean;
  shift: Shift | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditShiftModal = ({ open, shift, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Pre-fill on open / shift change.
  useEffect(() => {
    if (!open || !shift) return;
    form.setFieldsValue({
      startTime: shift.startTime ? dayjs(shift.startTime, 'HH:mm') : null,
      endTime: shift.endTime ? dayjs(shift.endTime, 'HH:mm') : null,
    });
  }, [open, shift, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    if (!shift) return;

    const newStart = values.startTime?.format('HH:mm');
    const newEnd = values.endTime?.format('HH:mm');

    // Diff-only payload (BR-11).
    const dto: AdminEditShiftDto = {};
    if (newStart && newStart !== shift.startTime) dto.startTime = newStart;
    if (newEnd && newEnd !== shift.endTime) dto.endTime = newEnd;

    if (!dto.startTime && !dto.endTime) {
      notification.warning({
        message: 'Cần thay đổi ít nhất một giờ trước khi lưu.',
      });
      return;
    }

    // BR-13: post-merge same-time guard.
    const finalStart = dto.startTime ?? shift.startTime;
    const finalEnd = dto.endTime ?? shift.endTime;
    if (sameTime(finalStart, finalEnd)) {
      notification.warning({
        message: 'Giờ bắt đầu và kết thúc không được trùng nhau.',
      });
      return;
    }

    setLoading(true);
    try {
      await AxiosUtils.patch(`${API.attendanceShifts}/${shift.id}`, dto);
      notification.success({ message: 'Đã cập nhật ca làm.' });
      onSuccess();
      handleClose();
    } catch (err: any) {
      // 404: row gone (concurrent soft-delete) → close + refresh, friendly toast.
      const raw = Array.isArray(err?.message) ? err.message[0] : err?.message;
      if (err?.statusCode === 404 || raw?.includes?.('SHIFT_NOT_FOUND')) {
        notification.warning({
          message: 'Ca làm không tồn tại hoặc đã bị xoá.',
        });
        onSuccess();
        handleClose();
        return;
      }
      notification.error({ message: mapShiftError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Sửa giờ ca"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {shift && (
        <>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Tên ca">{shift.name}</Descriptions.Item>
            <Descriptions.Item label="Phòng ban">
              {shift.department ? DEPARTMENT_LABEL[shift.department] || shift.department : '—'}
            </Descriptions.Item>
          </Descriptions>
          <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
            <Form.Item
              label="Giờ bắt đầu"
              name="startTime"
              rules={[{ required: true, message: 'Vui lòng chọn giờ bắt đầu.' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={5} />
            </Form.Item>
            <Form.Item
              label="Giờ kết thúc"
              name="endTime"
              rules={[{ required: true, message: 'Vui lòng chọn giờ kết thúc.' }]}
              extra="Cho phép ca xuyên đêm (kết thúc nhỏ hơn bắt đầu)."
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={5} />
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
              <CustomButton
                text="Lưu"
                bgColor="secondary-green"
                htmlType="submit"
                loading={loading}
              />
            </div>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default EditShiftModal;
